import {
  TerraUIMessageDataPartName,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import {
  activityItem,
  parentToolNameOf,
  textMatchesSubagentOrigin,
  type SubagentActivityItem,
} from './subagent-activity-items';
import {
  isSubagentToolPart,
  parentToolCallIdOfPart,
  subagentOwnedPartIndexes,
  toolCallIdOfPart,
} from './subagent-part-ownership';

export type { SubagentActivityItem } from './subagent-activity-items';
export type { SubagentOpenTarget } from './subagent-open-target';
export { openTargetFromTool } from './subagent-open-target';

export type SubagentActivityNoun = 'file' | 'search' | 'command' | 'action';

export type SubagentActivityCount = {
  noun: SubagentActivityNoun;
  count: number;
};

const INNER_ANALYZER_FILE_TOOLS = new Set([
  'grep',
  'read',
  'glob',
  'bash',
  'ls',
  'list-files',
  'write',
  'write-file',
  'edit',
  'read-file',
]);

function isCodeAnalyzerToolName(toolName: string): boolean {
  const normalized = toolName.replace(/_/g, '-').toLowerCase();

  return normalized === 'code-analyzer-tool' || normalized === 'code-analyzer';
}

function isInnerAnalyzerFileTool(toolName: string): boolean {
  return INNER_ANALYZER_FILE_TOOLS.has(
    toolName.replace(/_/g, '-').toLowerCase(),
  );
}

const ACTIVITY_NOUNS: Record<string, SubagentActivityNoun> = {
  read: 'file',
  'read-file': 'file',
  'list-files': 'file',
  ls: 'file',
  glob: 'file',
  write: 'file',
  'write-file': 'file',
  edit: 'file',
  grep: 'search',
  bash: 'command',
};

type SubagentActivity = {
  items: SubagentActivityItem[];
  counts: SubagentActivityCount[];
};

const EMPTY_ACTIVITY: SubagentActivity = { items: [], counts: [] };

export function collectSubagentActivity({
  messages,
  parentToolCallId,
}: {
  messages: TerraUIMessage[];
  parentToolCallId: string;
}): SubagentActivity {
  const parentMessageIndex = messages.findIndex((candidate) =>
    candidate.parts?.some(
      (part) => toolCallIdOfPart(part) === parentToolCallId,
    ),
  );
  const parentMessage = messages[parentMessageIndex];

  if (!parentMessage) {
    return EMPTY_ACTIVITY;
  }

  const head = activityFromMessage({
    message: parentMessage,
    parentToolCallId,
  });
  const parentPart = parentMessage.parts?.find(
    (part) => toolCallIdOfPart(part) === parentToolCallId,
  );
  const relocated = relocatedChildParts({
    messages: messages.slice(parentMessageIndex + 1),
    parentToolCallId,
    ownedIds: ownedToolCallIds(head),
  });

  if (relocated.length === 0) {
    return head;
  }

  const tail = activityFromMessage({
    message: {
      id: `${parentMessage.id}:relocated`,
      role: 'assistant',
      parts: parentPart ? [parentPart, ...relocated] : relocated,
    },
    parentToolCallId,
  });

  return mergeActivities(head, tail);
}

export function indexSubagentActivities({
  messages,
  parentToolCallIds,
}: {
  messages: readonly TerraUIMessage[];
  parentToolCallIds: readonly string[];
}): ReadonlyMap<string, SubagentActivity> {
  const pending = new Set(parentToolCallIds);
  const activities = new Map<string, SubagentActivity>();

  if (pending.size === 0) {
    return activities;
  }

  for (const message of messages) {
    const parentsHere: string[] = [];

    for (const part of message.parts ?? []) {
      const toolCallId = toolCallIdOfPart(part);

      if (!toolCallId || !pending.has(toolCallId)) {
        continue;
      }

      pending.delete(toolCallId);
      parentsHere.push(toolCallId);
    }

    for (const parentToolCallId of parentsHere) {
      activities.set(
        parentToolCallId,
        collectSubagentActivity({
          messages: [...messages],
          parentToolCallId,
        }),
      );
    }

    if (pending.size === 0) {
      break;
    }
  }

  return activities;
}

function relocatedChildParts({
  messages,
  parentToolCallId,
  ownedIds,
}: {
  messages: readonly TerraUIMessage[];
  parentToolCallId: string;
  ownedIds: Set<string>;
}): NonNullable<TerraUIMessage['parts']> {
  const owned = new Set([parentToolCallId, ...ownedIds]);
  const relocated: NonNullable<TerraUIMessage['parts']> = [];

  for (const message of messages) {
    for (const part of message.parts ?? []) {
      const parent = parentToolCallIdOfPart(part);

      if (!parent || !owned.has(parent)) {
        continue;
      }

      relocated.push(part);
      const toolCallId = toolCallIdOfPart(part);

      if (toolCallId) {
        owned.add(toolCallId);
      }
    }
  }

  return relocated;
}

function ownedToolCallIds(activity: SubagentActivity): Set<string> {
  return new Set(
    activity.items.flatMap((item) => (item.kind === 'tool' ? [item.id] : [])),
  );
}

function mergeActivities(
  head: SubagentActivity,
  tail: SubagentActivity,
): SubagentActivity {
  const byId = new Map(head.items.map((item) => [item.id, item]));
  const order = head.items.map((item) => item.id);

  for (const item of tail.items) {
    if (!byId.has(item.id)) {
      order.push(item.id);
    }

    byId.set(item.id, item);
  }

  const items = order.flatMap((id) => {
    const item = byId.get(id);

    return item ? [item] : [];
  });

  return { items, counts: summarizeActivity(items) };
}

function activityFromMessage({
  message,
  parentToolCallId,
}: {
  message: TerraUIMessage;
  parentToolCallId: string;
}): SubagentActivity {
  const parts = message.parts;

  if (!parts) {
    return EMPTY_ACTIVITY;
  }

  const parentIndex = parts.findIndex(
    (part) => toolCallIdOfPart(part) === parentToolCallId,
  );
  const parentToolName = parentToolNameOf({ parts, parentToolCallId });
  const ownedIndexes = subagentOwnedPartIndexes({ parts, parentToolCallId });
  const ownedIndexSet = new Set(ownedIndexes);
  const normalizedParent = normalizeToolName(parentToolName);
  const sameTypeSiblingBoundary = parts.findIndex((part, index) => {
    if (index <= parentIndex || !isSubagentToolPart(part)) {
      return false;
    }

    return normalizeToolName(toolNameOfPart(part)) === normalizedParent;
  });
  const originTextIndexes = parts.flatMap((part, index) => {
    if (
      index <= parentIndex ||
      ownedIndexSet.has(index) ||
      part.type !== 'text' ||
      (sameTypeSiblingBoundary >= 0 && index >= sameTypeSiblingBoundary)
    ) {
      return [];
    }

    return textMatchesSubagentOrigin({ part, parentToolName }) ? [index] : [];
  });
  const partIndexes = [...ownedIndexes, ...originTextIndexes].sort(
    (left, right) => left - right,
  );
  const items = partIndexes.flatMap((partIndex, index) => {
    const part = parts[partIndex];
    const item = part
      ? activityItem({ part, index, idScope: message.id })
      : undefined;

    return item ? [item] : [];
  });
  const hasCodeAnalyzer = items.some(
    (item) => item.kind === 'tool' && isCodeAnalyzerToolName(item.toolName),
  );
  const visibleItems = hasCodeAnalyzer
    ? items.filter(
        (item) =>
          item.kind !== 'tool' || !isInnerAnalyzerFileTool(item.toolName),
      )
    : items;

  return { items: visibleItems, counts: summarizeActivity(items) };
}

function normalizeToolName(toolName: string | undefined): string {
  return (toolName ?? '').replace(/_/g, '-').toLowerCase();
}

function toolNameOfPart(
  part: NonNullable<TerraUIMessage['parts']>[number],
): string | undefined {
  if (part.type !== TerraUIMessageDataPartName) {
    return undefined;
  }

  const toolName = Reflect.get(part.data, 'toolName');

  return typeof toolName === 'string' && toolName.trim()
    ? toolName.trim()
    : undefined;
}

function summarizeActivity(
  items: SubagentActivityItem[],
): SubagentActivityCount[] {
  const counts = new Map<SubagentActivityNoun, number>();
  const order: SubagentActivityNoun[] = [];

  for (const item of items) {
    if (item.kind !== 'tool') {
      continue;
    }

    const noun =
      ACTIVITY_NOUNS[item.toolName.replace(/_/g, '-').toLowerCase()] ??
      'action';

    if (!counts.has(noun)) {
      order.push(noun);
    }

    counts.set(noun, (counts.get(noun) ?? 0) + 1);
  }

  return order.map((noun) => ({
    noun,
    count: counts.get(noun) ?? 0,
  }));
}
